/**
 * Auth context: login, logout, token storage, optional verify-token on load.
 */
import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import client, { TOKEN_KEY } from '../api/client';

const COMPANY_ID_KEY = 'company_id';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const login = useCallback(async (username, password, companyId) => {
    const res = await client.post('/login', {
      username: username.trim(),
      password,
      company_id: String(companyId).trim(),
    });
    const data = res.data;
    if (!data.success || !data.token) {
      return { success: false, message: data.message || 'Login failed' };
    }
    // Token is now stored in the httpOnly auth cookie set by the server.
    // No longer written to localStorage to prevent XSS token theft.
    if (data.user?.company_id != null) {
      localStorage.setItem(COMPANY_ID_KEY, String(data.user.company_id));
    }
    setUser(data.user);
    return { success: true, user: data.user };
  }, []);

  const logout = useCallback(() => {
    const doClear = () => {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(COMPANY_ID_KEY);
      localStorage.removeItem('branch_id');
      setUser(null);
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
    };
    // Always call the server to clear the httpOnly auth cookie,
    // even when no localStorage token exists (cookie-only session).
    client.post('/api/logout').then(doClear).catch(doClear);
  }, []);

  const verifyToken = useCallback(async () => {
    // Attempt session restore via httpOnly auth cookie (withCredentials: true).
    // Falls back to Authorization header if a localStorage token still exists.
    try {
      const res = await client.get('/verify-token', { timeout: 5000 });
      if (res.data?.success && res.data?.user) {
        setUser(res.data.user);
      } else {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(COMPANY_ID_KEY);
        localStorage.removeItem('branch_id');
      }
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(COMPANY_ID_KEY);
      localStorage.removeItem('branch_id');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    verifyToken();
  }, [verifyToken]);

  const value = { user, loading, login, logout };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
